/*
 * sidebar
 */
import { Link } from "@tanstack/react-router"
import { cn } from "@psychology/react-ui"
import { Button, Divider } from "@psychology/react-ui"
import type { ReactNode } from "react"
import { Menu } from "./sidebar-menu"
import { SidebarToggle } from "./sidebar-toggle"
import { useSidebarToggle } from "./use-sidebar-toggle"

export type SidebarProps = {
	title: string
	TitleIcon: any
	bottomContent?: ReactNode
	titleHref?: string
	hideToggle?: boolean
	children?: ReactNode
	className?: string
}

export function Sidebar({
	title,
	titleHref,
	TitleIcon,
	bottomContent,
	hideToggle = false,
	children,
	className,
}: SidebarProps) {
	const sidebar = useSidebarToggle()
	const TitleContainer = titleHref ? Link : "div"

	return (
		<aside
			className={cn(
				className,
				"text-paper-foreground bg-paper border-border border-r",
				"left-0 top-0 z-20 h-full w-full -translate-x-full transition-[max-width] duration-300 ease-in-out lg:translate-x-0",
				"hidden lg:block",
				!sidebar.isOpen
					? "max-w-(--sidebar-closed-width)"
					: "max-w-(--sidebar-open-width)",
			)}
		>
			{!hideToggle && (
				<SidebarToggle isOpen={sidebar.isOpen} setIsOpen={sidebar.setIsOpen} />
			)}
			<div
				className={
					"relative flex h-full flex-col overflow-y-auto px-0 py-2 shadow-md dark:shadow-zinc-800"
				}
			>
				<Button
					className={cn([
						"transition-transform duration-300 ease-in-out",
						!titleHref ? "text-foreground cursor-default" : "",
					])}
					variant={titleHref ? "link" : "none"}
					asChild
				>
					<TitleContainer
						className={"flex items-center gap-2 h-auto pb-4"}
						to={titleHref as any}
					>
						{TitleIcon}
						<h1
							className={cn(
								"whitespace-nowrap text-lg font-bold transition-[transform,opacity,display] duration-300 ease-in-out",
								!sidebar.isOpen
									? "hidden -translate-x-96 opacity-0"
									: "translate-x-0 opacity-100",
							)}
						>
							{title}
						</h1>
					</TitleContainer>
				</Button>
				<Divider />
				<Menu bottomContent={bottomContent}>{children}</Menu>
			</div>
		</aside>
	)
}
